import { useState } from 'react';
import { GRADES, LANGUAGES, LIMITS, USERNAME_PATTERN, passwordProblems } from '@msarat/shared';
import { Alert, Field, PasswordField, SubmitButton } from '@msarat/theme/components';

import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.jsx';

/**
 * Students section: the parent's list, plus add, edit, and remove.
 *
 * The parent owns the child's credentials, because a student has no email and
 * therefore no way to recover an account alone. The copy says so wherever a
 * password is set, so the responsibility is never a surprise.
 */
export function Students({ students, loading, onChanged }) {
  const { t } = useI18n();
  const [mode, setMode] = useState({ kind: 'idle' });
  const [notice, setNotice] = useState('');

  function close(message) {
    setMode({ kind: 'idle' });
    if (message) setNotice(message);
    onChanged?.();
  }

  return (
    <section className="panel">
      <div className="panel__head">
        <h1 style={{ fontSize: 'var(--text-xl)' }}>{t('students.title')}</h1>
        {mode.kind === 'idle' ? (
          <button className="button button--primary" type="button" onClick={() => setMode({ kind: 'create' })}>
            {t('students.add')}
          </button>
        ) : null}
      </div>

      {notice ? <Alert kind="success">{notice}</Alert> : null}

      {mode.kind === 'create' ? (
        <StudentForm onDone={() => close(t('students.added'))} onCancel={() => setMode({ kind: 'idle' })} />
      ) : null}

      {mode.kind === 'edit' ? (
        <StudentForm
          student={mode.student}
          onDone={() => close(t('students.saved'))}
          onCancel={() => setMode({ kind: 'idle' })}
        />
      ) : null}

      {mode.kind === 'delete' ? (
        <DeleteConfirm
          student={mode.student}
          onDone={() => close(t('students.removed'))}
          onCancel={() => setMode({ kind: 'idle' })}
        />
      ) : null}

      {loading ? (
        <p className="empty" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : students.length === 0 && mode.kind === 'idle' ? (
        <p className="empty">{t('students.empty')}</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {students.map((student) => (
            <li key={student.id} className="skill-row">
              <div>
                <strong>{student.name}</strong>
                <small>
                  <span dir="ltr">{student.username}</span> ·{' '}
                  {t('students.grade', { grade: student.grade })}
                </small>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => setMode({ kind: 'edit', student })}
                >
                  {t('students.edit')}
                </button>
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => setMode({ kind: 'delete', student })}
                >
                  {t('students.remove')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One form for both adding and editing.
 *
 * Editing deliberately cannot change the username: a child has learned to type
 * it, and changing it silently locks them out of the only credential they know.
 * The password is optional when editing — left blank, it is untouched.
 */
function StudentForm({ student, onDone, onCancel }) {
  const { t, language } = useI18n();
  const editing = Boolean(student);
  const [values, setValues] = useState({
    name: student?.name ?? '',
    username: student?.username ?? '',
    password: '',
    grade: student?.grade ?? '5',
    // A new student inherits the parent's current interface language as a
    // sensible default; it is editable because a child may well read better in
    // the other one.
    language: student?.language ?? language,
  });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [pending, setPending] = useState(false);

  const set = (key) => (value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  function validate() {
    const found = {};
    if (!values.name.trim()) found.name = t('valid.required');
    if (!editing && !USERNAME_PATTERN.test(values.username.trim().toLowerCase())) {
      found.username = t('valid.username_format');
    }
    if (!GRADES.includes(values.grade)) found.grade = t('valid.grade_invalid');

    // On create the password is required; on edit it means "change it to this".
    const passwordRequired = !editing;
    if (passwordRequired || values.password) {
      const problems = passwordProblems(values.password);
      if (problems.includes('too_short')) {
        found.password = t('valid.password_too_short', { min: LIMITS.password.min });
      } else if (problems.length > 0) {
        found.password = t('valid.password_invalid');
      }
    }
    return found;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError('');

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setPending(true);
    try {
      if (editing) {
        const payload = { name: values.name.trim(), grade: values.grade, language: values.language };
        if (values.password) payload.password = values.password;
        await api.updateStudent(student.id, payload);
      } else {
        await api.createStudent({
          name: values.name.trim(),
          username: values.username.trim().toLowerCase(),
          password: values.password,
          grade: values.grade,
          language: values.language,
        });
      }
      onDone?.();
    } catch (error) {
      const fieldErrors = error.fieldErrors?.(t, { min: LIMITS.password.min }) ?? {};
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length === 0) setFormError(error.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <form className="card stack" onSubmit={handleSubmit} noValidate style={{ marginBlockEnd: 'var(--space-5)' }}>
      <h2 style={{ fontSize: 'var(--text-lg)' }}>
        {editing ? t('studentForm.editTitle', { name: student.name }) : t('studentForm.addTitle')}
      </h2>
      <Alert kind="error">{formError}</Alert>

      <Field
        id="student-name"
        label={t('studentForm.name')}
        value={values.name}
        onChange={set('name')}
        error={errors.name}
        hint={t('studentForm.nameHint')}
        required
        maxLength={LIMITS.studentName}
      />

      {editing ? (
        <div className="field">
          <span className="field__label">{t('studentForm.username')}</span>
          <p dir="ltr" style={{ textAlign: 'start' }}>
            {student.username}
          </p>
          <span className="field__hint">{t('studentForm.usernameLocked')}</span>
        </div>
      ) : (
        <Field
          id="student-username"
          label={t('studentForm.username')}
          value={values.username}
          onChange={set('username')}
          error={errors.username}
          hint={t('studentForm.usernameHint')}
          required
          ltr
          autoComplete="off"
          maxLength={LIMITS.username}
        />
      )}

      <Field id="student-grade" label={t('studentForm.gradeLabel')} value={values.grade} onChange={set('grade')} error={errors.grade} required>
        <select
          className="select"
          id="student-grade"
          value={values.grade}
          onChange={(event) => set('grade')(event.target.value)}
          aria-invalid={errors.grade ? 'true' : undefined}
        >
          {GRADES.map((grade) => (
            <option key={grade} value={grade}>
              {t('students.grade', { grade })}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id="student-language"
        label={t('studentForm.language')}
        value={values.language}
        onChange={set('language')}
        error={errors.language}
        hint={t('studentForm.languageHint')}
        required
      >
        <select
          className="select"
          id="student-language"
          value={values.language}
          onChange={(event) => set('language')(event.target.value)}
          aria-invalid={errors.language ? 'true' : undefined}
        >
          {LANGUAGES.map((code) => (
            <option key={code} value={code}>
              {code === 'ar' ? t('common.arabic') : t('common.english')}
            </option>
          ))}
        </select>
      </Field>

      <PasswordField
        id="student-password"
        label={editing ? t('studentForm.newPassword') : t('studentForm.password')}
        value={values.password}
        onChange={set('password')}
        error={errors.password}
        hint={
          editing
            ? t('studentForm.passwordHintEdit')
            : t('studentForm.passwordHintCreate', { min: LIMITS.password.min })
        }
        required={!editing}
        autoComplete="new-password"
        showLabel={t('common.showPassword')}
        hideLabel={t('common.hidePassword')}
      />

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <SubmitButton pending={pending}>
          {editing ? t('studentForm.saveEdits') : t('studentForm.save')}
        </SubmitButton>
        <button className="button button--secondary" type="button" onClick={onCancel} disabled={pending}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * Removal confirmation.
 *
 * Requires typing the student's name. Removing a child takes away their access
 * immediately, so a mis-click should not be enough — and the copy states what
 * actually happens rather than a generic "are you sure?".
 */
function DeleteConfirm({ student, onDone, onCancel }) {
  const { t } = useI18n();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);

  const confirmed = typed.trim() === student.name.trim();

  async function handleDelete() {
    setPending(true);
    setError('');
    try {
      await api.deleteStudent(student.id);
      onDone?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="card stack" style={{ marginBlockEnd: 'var(--space-5)' }}>
      <h2 style={{ fontSize: 'var(--text-lg)' }}>{t('delete.title', { name: student.name })}</h2>
      <Alert kind="error">{error}</Alert>

      <p>{t('delete.body', { name: student.name })}</p>

      <Field
        id="confirm-name"
        label={t('delete.confirmLabel', { name: student.name })}
        value={typed}
        onChange={setTyped}
        required
      />

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button
          className="button button--primary"
          type="button"
          onClick={handleDelete}
          disabled={!confirmed || pending}
          style={confirmed ? { background: 'var(--color-danger)' } : undefined}
        >
          {pending ? t('delete.pending') : t('delete.submit')}
        </button>
        <button className="button button--secondary" type="button" onClick={onCancel} disabled={pending}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
